// The rules every platform applies to a file once it's been read
// (spec/fixtures/import/README.md, "Rules"). Pure, so they're unit-tested
// against the shared test files.

import type {
  ImportAction,
  ImportIntent,
  ImportLine,
  ImportPoint,
  ImportPreview,
  ImportWaypoint,
  RawFile,
  RawLine,
  SkipReason,
  TimesReason
} from './importTypes';

const R = 6371008.8;
const toRad = (d: number): number => (d * Math.PI) / 180;

export const haversineM = (a: ImportPoint, b: ImportPoint): number => {
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const dla = la2 - la1;
  const dlo = toRad(b.lon - a.lon);
  const h = Math.sin(dla / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dlo / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

// Within each segment only: a GPS gap between segments isn't walked distance.
export const lineLengthM = (segments: ImportPoint[][]): number =>
  segments.reduce((sum, seg) => sum + seg.slice(1).reduce((s, p, i) => s + haversineM(seg[i], p), 0), 0);

const AUTO_NAME = /^\s*((wpt|wp|waypoint|point|pt)\s*[-_ ]?)?\d+\s*$/i;

// Rule 1: usable times. Generated ones (identical, or a constant planner speed) don't count.
export function timesOf(points: ImportPoint[]): { coverage: number; usable: boolean; reason: TimesReason } {
  if (!points.length) return { coverage: 0, usable: false, reason: null };
  const timed = points.filter(p => p.time != null);
  const coverage = timed.length / points.length;
  if (coverage < 0.9) return { coverage, usable: false, reason: timed.length ? 'partial_times' : null };
  for (let i = 1; i < timed.length; i++) {
    if ((timed[i].time as number) < (timed[i - 1].time as number)) return { coverage, usable: false, reason: 'partial_times' };
  }
  if (((timed[timed.length - 1].time as number) - (timed[0].time as number)) / 1000 < 60) return { coverage, usable: false, reason: 'partial_times' };
  if (isGenerated(timed)) return { coverage, usable: false, reason: 'generated_times' };
  return { coverage, usable: true, reason: null };
}

function isGenerated(timed: ImportPoint[]): boolean {
  if (timed.length < 11) return false;
  if (timed.every(p => p.time === timed[0].time)) return true;
  const speeds: number[] = [];
  for (let i = 1; i < timed.length; i++) {
    const dt = ((timed[i].time as number) - (timed[i - 1].time as number)) / 1000;
    if (dt > 0) speeds.push(haversineM(timed[i - 1], timed[i]) / dt);
  }
  if (speeds.length < 10) return false;
  const mean = speeds.reduce((a, b) => a + b, 0) / speeds.length;
  if (mean === 0) return true;
  const sd = Math.sqrt(speeds.reduce((a, s) => a + (s - mean) ** 2, 0) / speeds.length);
  return sd / mean < 0.02;
}

// Rule 2: a route that's the course of a recorded track.
function duplicateOf(route: RawLine, lines: RawLine[]): number | null {
  const rpts = route.segments.flat();
  if (!rpts.length) return null;
  for (let ti = 0; ti < lines.length; ti++) {
    if (lines[ti].kind !== 'track') continue;
    const tpts = lines[ti].segments.flat();
    if (!tpts.length) continue;
    const near = rpts.filter(p => tpts.some(q => haversineM(p, q) <= 30)).length;
    if (near / rpts.length >= 0.8) return ti;
  }
  return null;
}

// Rule 3: unnamed waypoints sitting on the line.
function repeatsLine(name: string, point: ImportPoint, vertices: ImportPoint[]): boolean {
  const unnamed = !name.trim() || AUTO_NAME.test(name);
  return unnamed && vertices.some(v => haversineM(point, v) <= 10);
}

/** A track that becomes a recording: usable times, and not a course (contract rules 1 and 2). */
export const isRecordedTrack = (l: ImportLine): boolean => l.kind === 'track' && l.timesUsable && l.duplicateOf === null;

export function analyse(raw: RawFile): ImportPreview {
  const lines: ImportLine[] = raw.lines.map(l => {
    const points = l.segments.flat();
    const t = l.kind === 'track' ? timesOf(points) : { coverage: 0, usable: false, reason: null as TimesReason };
    return {
      ...l,
      pointCount: points.length,
      distanceM: lineLengthM(l.segments),
      timeCoverage: t.coverage,
      timesUsable: t.usable,
      timesReason: t.reason,
      duplicateOf: null
    };
  });
  raw.lines.forEach((l, i) => {
    if (l.kind === 'route') lines[i].duplicateOf = duplicateOf(l, raw.lines);
  });

  const vertices = raw.lines.flatMap(l => l.segments.flat());
  const waypoints: ImportWaypoint[] = raw.waypoints.map(w => ({ ...w, repeatsLine: repeatsLine(w.name, w.point, vertices) }));
  const repeats = waypoints.filter(w => w.repeatsLine).length;
  const kept = waypoints.length - repeats;

  if (!lines.length && !waypoints.length) return emptyPreview(raw.format, 'empty');

  // Rules 4 and 5.
  const hasLines = lines.length > 0;
  const recording = lines.some(isRecordedTrack);
  // Both: the timed tracks become recordings and every other line one planned hike.
  const both = recording && lines.some(l => l.duplicateOf === null && !isRecordedTrack(l));
  const canBe: ImportAction[] = [];
  if (hasLines) canBe.push('planned');
  if (recording) canBe.push('recording');
  if (both) canBe.push('both');
  if (kept > 0) canBe.push('waypoints');
  if (!hasLines && kept > 1) canBe.push('fromwpts');
  const recommended: ImportAction | null = both ? 'both' : recording ? 'recording' : hasLines ? 'planned' : kept > 0 ? 'waypoints' : null;

  return {
    format: raw.format,
    error: null,
    lines,
    waypoints,
    waypointCounts: { count: waypoints.length, repeatsLine: repeats, kept },
    canBe,
    recommended
  };
}

export const emptyPreview = (format: ImportPreview['format'], error: ImportPreview['error']): ImportPreview => ({
  format,
  error,
  lines: [],
  waypoints: [],
  waypointCounts: { count: 0, repeatsLine: 0, kept: 0 },
  canBe: [],
  recommended: null
});

function skipReason(p: ImportPreview, intent: ImportIntent): SkipReason {
  if (intent === 'recording') {
    if (!p.lines.length) return 'only_waypoints';
    const tracks = p.lines.filter(l => l.kind === 'track');
    if (tracks.some(l => l.timesReason === 'generated_times')) return 'generated_times';
    if (tracks.some(l => l.timesReason === 'partial_times')) return 'partial_times';
    return 'no_times';
  }
  if (intent === 'planned') return 'only_waypoints';
  return 'no_waypoints';
}

// Rule 6: the file's type decides — a timed track is a recording, any other line a
// planned hike, a waypoints-only file a waypoint group. The Waypoints screen keeps a
// file's waypoints; a hike through the waypoints is only ever an explicit choice.
// `reason` says why the file doesn't fit the start screen, when it doesn't.
// A bagged-peaks CSV goes to My Peaks from every screen, and that needs no reason.
export function defaultFor(p: ImportPreview, intent: ImportIntent): { action: ImportAction; reason: SkipReason | null } {
  if (p.error) return { action: 'skip', reason: null };
  if (p.canBe.includes('peaks')) return { action: 'peaks', reason: null };
  const reason = intent !== 'none' && !p.canBe.includes(intent) ? skipReason(p, intent) : null;
  if (intent === 'waypoints' && p.canBe.includes('waypoints')) return { action: 'waypoints', reason };
  return { action: p.recommended ?? 'skip', reason };
}

// The smallest and largest of many numbers, in one pass. Never Math.min(...values):
// spreading a long array passes every value as an argument, and past ~100k (a long
// recording) the engine throws "Maximum call stack size exceeded" (#1833).
export function numberRange(values: number[]): { min: number; max: number } | null {
  if (!values.length) return null;
  let min = values[0];
  let max = values[0];
  for (let i = 1; i < values.length; i++) {
    if (values[i] < min) min = values[i];
    if (values[i] > max) max = values[i];
  }
  return { min, max };
}

// Appends in place without spreading `items` as arguments (see numberRange).
export function appendAll<T>(target: T[], items: readonly T[]): T[] {
  for (const item of items) target.push(item);
  return target;
}
